import { KeyboardEvent, memo } from 'react';
import { css } from '@emotion/react';
import { Handle, NodeProps, Position } from 'reactflow';
import { color, Icon, spacing, Typography, typography } from '@uniquegood/realworld-studio-design';

import type { MissionNodeData } from '../types';
import { MISSION_NODE_WIDTH } from '../constants';

function MissionNode({ data }: NodeProps<MissionNodeData>) {
  const { mission, missionIndex, isSelected, onSelectMission, onAddQuest } = data;

  const handleSelectMission = () => {
    onSelectMission(mission.id);
  };

  const handleHeaderKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      event.stopPropagation();
      handleSelectMission();
    }
  };

  return (
    <div css={[NodeContainer, isSelected && NodeContainerSelected]}>
      <Handle type="target" position={Position.Left} css={HandleStyle} />

      <div
        role="button"
        tabIndex={0}
        css={NodeHeader}
        onKeyDown={handleHeaderKeyDown}
        onClick={(e) => {
          e.stopPropagation();
          handleSelectMission();
        }}
      >
        <div css={HeaderTopRow}>
          <span css={MissionBadge}>{`MISSION ${String(missionIndex + 1).padStart(2, '0')}`}</span>
          <Typography as="span" type="caption" textColor="subdued">
            {mission.quests?.length ?? 0}개 퀘스트
          </Typography>
        </div>
        <Typography as="h6" type="button" cssStyle={NodeTitle}>
          {mission.name || '이름 없는 미션'}
        </Typography>
        {mission.coverImageUrl && <img src={mission.coverImageUrl} alt="" css={CoverThumbnail} />}
      </div>

      <div css={Footer}>
        <Typography as="span" type="caption" textColor="subdued">
          퀘스트는 아래 노드에서 편집할 수 있어요
        </Typography>
        <button
          type="button"
          css={AddQuestButton}
          onClick={(e) => {
            e.stopPropagation();
            onAddQuest(mission.id);
          }}
        >
          <Icon icon="plus_solid" size="12px" color={color.action_primary_default} />
          <span>새 퀘스트</span>
        </button>
      </div>

      <Handle type="source" position={Position.Right} css={HandleStyle} />
    </div>
  );
}

export default memo(MissionNode);

const NodeContainer = css`
  width: ${MISSION_NODE_WIDTH}px;
  border-radius: ${spacing.borderRadius.large};
  border: 2px solid ${color.border_default_subdued};
  background: ${color.surface_default_default};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  cursor: default;
  transition: border-color 0.15s, box-shadow 0.15s;

  &:hover {
    border-color: ${color.border_primary_default};
  }
`;

const NodeContainerSelected = css`
  border-color: ${color.action_primary_default};
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12), 0 0 0 2px ${color.action_primary_default}33;
`;

const HandleStyle = css`
  && {
    width: 10px;
    height: 10px;
    background-color: ${color.action_primary_default};
    border: 2px solid ${color.white};
  }
`;

const NodeHeader = css`
  padding: ${spacing.common.medium};
  padding-bottom: ${spacing.common.small};
  cursor: pointer;
  border-bottom: 1px solid ${color.border_default_subdued};

  &:hover {
    background-color: ${color.surface_default_hovered};
  }
`;

const HeaderTopRow = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

const MissionBadge = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 999px;
  height: 22px;
  padding: 0 ${spacing.common.small};
  color: ${color.white};
  background-color: ${color.action_primary_default};
  font-size: 11px;
  font-weight: ${typography.weight.bold};
  letter-spacing: 0.04em;
  flex-shrink: 0;
`;

const NodeTitle = css`
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-bottom: 6px;
`;

const CoverThumbnail = css`
  width: 100%;
  height: 64px;
  object-fit: cover;
  border-radius: ${spacing.borderRadius.small};
  margin-bottom: 4px;
`;

const Footer = css`
  padding: ${spacing.common.small};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.common.small};
`;

const AddQuestButton = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px ${spacing.common.small};
  border-radius: ${spacing.borderRadius.small};
  border: 1px dashed ${color.border_default_subdued};
  background: transparent;
  cursor: pointer;
  color: ${color.action_primary_default};
  font-size: ${typography.size.s1};
  font-weight: ${typography.weight.medium};
  transition: background-color 0.1s, border-color 0.1s;
  white-space: nowrap;

  &:hover {
    background-color: ${color.surface_default_hovered};
    border-color: ${color.action_primary_default};
  }
`;
